import React, { useState, useEffect, useMemo } from 'react';
import { Message, Table, Loader, Button, Icon, Checkbox, Confirm, Header } from 'semantic-ui-react';
import axios from 'axios';
import RequestDetailsModal from './RequestDetailsModal';
import FilterPanel from './FilterPanel';
import PaginationFooter from './PaginationFooter';
import { useTablePreferences } from '../hooks/useTablePreferences';
import { useRequestFilters } from '../hooks/useRequestFilters';
import { formatStatus, getStatusColor, getCloudName, getSubmittedBy, isRequestForm3, formatTimestamp, highlightSearchTerms, getBatchBadges } from '../utils/requestHelpers';
import { useTimezone } from '../hooks/useTimezone';
import { api } from '../api/apiClient';

const SuperAdminHistory = ({ userRoles = [] }) => {
    const userTimezone = useTimezone(); // Listen to timezone changes for re-renders
    const canDelete = userRoles.includes('DEVOPS');
    const [allHistory, setAllHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // null for bulk, or requestId for single

    // Sorting state for DevOps history table
    const [sortColumn, setSortColumn] = useState('timestamp');
    const [sortDirection, setSortDirection] = useState('descending'); // 'ascending' | 'descending'

    // Use reusable hooks
    const { rowsPerPage, tableDensity, activePage, setActivePage } = useTablePreferences();
    const {
        // Basic filters
        statusFilter,
        cloudNameFilter,
        categoryFilter,
        requestIdFilter,
        // Advanced filters
        globalSearch,
        selectedStatuses,
        selectedCategories,
        dateRangeStart,
        dateRangeEnd,
        submittedByFilter,
        // Saved filters
        savedFilters,
        filterPresetName,
        availableStatuses,
        availableCategories,
        // Setters
        setStatusFilter,
        setCloudNameFilter,
        setCategoryFilter,
        setRequestIdFilter,
        setGlobalSearch,
        setSelectedStatuses,
        setSelectedCategories,
        setDateRangeStart,
        setDateRangeEnd,
        setSubmittedByFilter,
        setFilterPresetName,
        // Actions
        hasActiveFilters,
        filteredRequests: filteredHistory,
        resetFilters,
        saveCurrentFilters,
        loadFilterPreset,
        deleteFilterPreset
    } = useRequestFilters(allHistory, 'super-admin-history');

    // Calculate active filter count for badge
    const activeFilterCount = [
        statusFilter,
        cloudNameFilter,
        categoryFilter,
        requestIdFilter,
        globalSearch,
        selectedStatuses.length,
        selectedCategories.length,
        dateRangeStart,
        dateRangeEnd,
        submittedByFilter
    ].filter(v => v && v !== 0).length;

    // Reset page when filters change
    const handleFilterChange = () => {
        setActivePage(1);
    };

    const handleResetFilters = () => {
        resetFilters();
        setActivePage(1);
    };

    const fetchAllHistory = async () => {
        try {
            setLoading(true);
            const response = await axios.get('https://qa-tenantbridge.parablu.com/api/requests', {
                withCredentials: true
            });

            const validRequests = response.data.requests.filter(request => {
                if (!request || !request.id) {
                    console.warn('Skipping invalid request in DevOps history:', request);
                    return false;
                }

                return true;
            });

            // Show all requests including withdrawn ones (no filtering for withdrawals)
            const filteredRequests = validRequests;

            const sortedHistory = filteredRequests.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            setAllHistory(sortedHistory);
            setError(null);
        } catch (err) {
            console.error('Error fetching approval history:', err);
            setError('Failed to load approval history: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllHistory();
    }, []);

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
        } else {
            setSortColumn(column);
            // Default to descending for timestamp, ascending otherwise
            setSortDirection(column === 'timestamp' ? 'descending' : 'ascending');
        }
    };

    // Calculate pagination on sorted data
    const sortedHistory = useMemo(() => {
        const items = filteredHistory.slice();
        const directionFactor = sortDirection === 'descending' ? -1 : 1;

        const getFieldValue = (req, column) => {
            switch (column) {
                case 'timestamp':
                    return new Date(req.timestamp).getTime() || 0;
                case 'requestId':
                    return String(req.id || '').toLowerCase();
                case 'submittedBy':
                    return getSubmittedBy(req).toLowerCase();
                case 'cloudName':
                    return getCloudName(req).toLowerCase();
                case 'category':
                    return String(req.category || '').toLowerCase();
                case 'status':
                    return String(req.status || '').toLowerCase();
                case 'approvers':
                    return String(req.approver || '').toLowerCase();
                case 'comment':
                    return String(req.approverComment || '').toLowerCase();
                default:
                    return '';
            }
        };

        items.sort((a, b) => {
            const aVal = getFieldValue(a, sortColumn);
            const bVal = getFieldValue(b, sortColumn);

            if (aVal < bVal) return -1 * directionFactor;
            if (aVal > bVal) return 1 * directionFactor;
            return 0;
        });

        return items;
    }, [filteredHistory, sortColumn, sortDirection]);

    const totalPages = Math.max(1, Math.ceil(sortedHistory.length / Math.max(1, rowsPerPage)));
    const pageStart = (activePage - 1) * rowsPerPage;
    const pageEnd = pageStart + rowsPerPage;
    const visibleRequests = useMemo(() => {
        return sortedHistory.slice(pageStart, pageEnd);
    }, [sortedHistory, pageStart, pageEnd]);

    const handleSelectAll = (checked) => {
        if (checked) {
            const allIds = new Set(visibleRequests.map(r => r.id));
            setSelectedIds(allIds);
        } else {
            setSelectedIds(new Set());
        }
    };

    const handleSelectItem = (requestId, checked) => {
        const newSelected = new Set(selectedIds);
        if (checked) {
            newSelected.add(requestId);
        } else {
            newSelected.delete(requestId);
        }
        setSelectedIds(newSelected);
    };

    const handleDeleteClick = (requestId = null) => {
        setDeleteTarget(requestId);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            setIsDeleting(true);
            setError(null);

            if (deleteTarget) {
                // Single delete
                await api.requests.delete(deleteTarget);
                setAllHistory(prev => prev.filter(r => r.id !== deleteTarget));
                setSelectedIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(deleteTarget);
                    return newSet;
                });
            } else {
                // Bulk delete
                const idsToDelete = Array.from(selectedIds);
                if (idsToDelete.length > 0) {
                    await api.requests.deleteMultiple(idsToDelete);
                    setAllHistory(prev => prev.filter(r => !selectedIds.has(r.id)));
                    setSelectedIds(new Set());
                }
            }

            setDeleteConfirmOpen(false);
            setDeleteTarget(null);
        } catch (err) {
            console.error('Error deleting request(s):', err);
            setError(err.response?.data?.message || 'Failed to delete request(s)');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setDeleteTarget(null);
    };

    const isAllSelected = visibleRequests.length > 0 && visibleRequests.every(r => selectedIds.has(r.id));
    const isIndeterminate = visibleRequests.some(r => selectedIds.has(r.id)) && !isAllSelected;

    if (loading) return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
            <Loader active inline='centered'>Loading approval history...</Loader>
        </div>
    );

    if (error) return <Message negative>{error}</Message>;

    return (
        <div style={{
            marginTop: '12px',
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 180px)',
            minHeight: '500px',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Fixed Header */}
            <div style={{
                flexShrink: 0,
                backgroundColor: 'white',
                zIndex: 100,
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '1.35rem',
                            fontWeight: 700,
                            color: '#062D4D'
                        }}
                    >
                        <Icon name="tasks" color="blue" />
                        <span>All Approval History</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {canDelete && selectedIds.size > 0 && (
                            <Button
                                negative
                                loading={isDeleting}
                                disabled={isDeleting}
                                onClick={() => handleDeleteClick()}
                            >
                                <Icon name="trash" />
                                Delete Selected ({selectedIds.size})
                            </Button>
                        )}
                        <Button
                            icon
                            labelPosition="left"
                            onClick={() => setShowFilters(!showFilters)}
                            color={showFilters ? 'blue' : undefined}
                            primary={hasActiveFilters && !showFilters}
                        >
                            <Icon name="filter" />
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                            {hasActiveFilters && !showFilters && (
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginLeft: '8px',
                                    backgroundColor: '#fff',
                                    color: '#2185d0',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    minWidth: '22px',
                                    height: '22px',
                                    borderRadius: '12px',
                                    padding: '0 7px',
                                    lineHeight: 1,
                                    letterSpacing: '0.3px',
                                }}>
                                    {activeFilterCount}
                                </span>
                            )}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Sidebar Filter Panel */}
            {showFilters && (
                <FilterPanel
                        mode="advanced"
                        onClose={() => setShowFilters(false)}
                        // Basic filters
                        statusFilter={statusFilter}
                        cloudNameFilter={cloudNameFilter}
                        categoryFilter={categoryFilter}
                        requestIdFilter={requestIdFilter}
                        // Advanced filters
                        globalSearch={globalSearch}
                        selectedStatuses={selectedStatuses}
                        selectedCategories={selectedCategories}
                        dateRangeStart={dateRangeStart}
                        dateRangeEnd={dateRangeEnd}
                        submittedByFilter={submittedByFilter}
                        // Saved filters
                        savedFilters={savedFilters}
                        filterPresetName={filterPresetName}
                        availableStatuses={availableStatuses}
                        availableCategories={availableCategories}
                        // Basic callbacks
                        onStatusFilterChange={setStatusFilter}
                        onCloudNameFilterChange={setCloudNameFilter}
                        onCategoryFilterChange={setCategoryFilter}
                        onRequestIdFilterChange={setRequestIdFilter}
                        // Advanced callbacks
                        onGlobalSearchChange={setGlobalSearch}
                        onSelectedStatusesChange={setSelectedStatuses}
                        onSelectedCategoriesChange={setSelectedCategories}
                        onDateRangeStartChange={setDateRangeStart}
                        onDateRangeEndChange={setDateRangeEnd}
                        onSubmittedByFilterChange={setSubmittedByFilter}
                        // Saved filters callbacks
                        onFilterPresetNameChange={setFilterPresetName}
                        onSaveFilterPreset={saveCurrentFilters}
                        onLoadFilterPreset={loadFilterPreset}
                        onDeleteFilterPreset={deleteFilterPreset}
                        // Actions
                        onResetFilters={handleResetFilters}
                        hasActiveFilters={hasActiveFilters}
                        onFilterChange={handleFilterChange}
                    />
            )}

            {/* Scrollable Table Container */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'auto',
                minHeight: 0,
                marginTop: showFilters ? '0' : '12px',
                position: 'relative',
                zIndex: 1
            }}>
                {filteredHistory.length === 0 ? (
                    <Message info>
                        <Message.Header>No approval history found</Message.Header>
                        <p>{hasActiveFilters ? 'No records match the selected filters. Try adjusting your filters.' : 'There are no approval records in the system.'}</p>
                    </Message>
                ) : (
                    <>
                        <Table celled unstackable responsive sortable compact={tableDensity === 'compact'} size={tableDensity === 'compact' ? 'small' : undefined}>
                            <Table.Header style={{ position: 'sticky', top: 0, zIndex: 50, backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                <Table.Row>
                                    {canDelete && (
                                        <Table.HeaderCell style={{ backgroundColor: 'white', width: '50px' }}>
                                            <Checkbox
                                                checked={isAllSelected}
                                                indeterminate={isIndeterminate}
                                                onChange={(e, { checked }) => handleSelectAll(checked)}
                                            />
                                        </Table.HeaderCell>
                                    )}
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'timestamp' ? sortDirection : null}
                                        onClick={() => handleSort('timestamp')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Timestamp
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'requestId' ? sortDirection : null}
                                        onClick={() => handleSort('requestId')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Request ID
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'submittedBy' ? sortDirection : null}
                                        onClick={() => handleSort('submittedBy')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Submitted By
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'cloudName' ? sortDirection : null}
                                        onClick={() => handleSort('cloudName')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Cloud Name
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'category' ? sortDirection : null}
                                        onClick={() => handleSort('category')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Category
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'status' ? sortDirection : null}
                                        onClick={() => handleSort('status')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Current Status
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'approvers' ? sortDirection : null}
                                        onClick={() => handleSort('approvers')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Approver(s)
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'comment' ? sortDirection : null}
                                        onClick={() => handleSort('comment')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Approver Comment
                                    </Table.HeaderCell>
                                    <Table.HeaderCell style={{ backgroundColor: 'white' }}>Actions</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {visibleRequests.map(request => {
                                    const isSelected = selectedIds.has(request.id);
                                    
                                    // Collect all active search terms for highlighting
                                    const searchTerms = [
                                        globalSearch,
                                        requestIdFilter,
                                        cloudNameFilter,
                                        categoryFilter,
                                        statusFilter,
                                        submittedByFilter
                                    ].filter(term => term && term.trim());
                                    
                                    return (
                                        <Table.Row key={request.id || `request-${Math.random()}`}>
                                            {canDelete && (
                                                <Table.Cell>
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={(e, { checked }) => handleSelectItem(request.id, checked)}
                                                    />
                                                </Table.Cell>
                                            )}
                                            <Table.Cell>
                                                {highlightSearchTerms(formatTimestamp(request.timestamp), searchTerms)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {highlightSearchTerms(request.id || 'Unknown', searchTerms)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {highlightSearchTerms(getSubmittedBy(request), searchTerms)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {highlightSearchTerms(getCloudName(request), searchTerms)}
                                                {getBatchBadges(request).map((badge, idx) => (
                                                    <span
                                                        key={idx}
                                                        style={{
                                                            marginLeft: idx === 0 ? '8px' : '4px',
                                                            padding: '2px 6px',
                                                            backgroundColor: '#e1f5fe',
                                                            color: '#35B7F4',
                                                            borderRadius: '3px',
                                                            fontSize: '0.8em',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {badge}
                                                    </span>
                                                ))}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {highlightSearchTerms(
                                                    request.category === 'PROD' &&
                                                    request.formData?.requestForm3
                                                        ? 'PROD / POC'
                                                        : (request.category || 'Unknown'),
                                                    searchTerms
                                                )}
                                            </Table.Cell>
                                            <Table.Cell className={getStatusColor(request.status)}>
                                                {highlightSearchTerms(formatStatus(request.status), searchTerms)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {highlightSearchTerms(request.approver || '-', searchTerms)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {highlightSearchTerms(request.approverComment || '-', searchTerms)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'nowrap' }}>
                                                    <Button
                                                        size="small"
                                                        content="View Details"
                                                        onClick={() => {
                                                            setSelectedRequest(request);
                                                            setShowDetailsModal(true);
                                                        }}
                                                    />
                                                    {canDelete && (
                                                        <Button
                                                            size="small"
                                                            negative
                                                            icon="trash"
                                                            loading={isDeleting && deleteTarget === request.id}
                                                            disabled={isDeleting}
                                                            onClick={() => handleDeleteClick(request.id)}
                                                            style={{ flexShrink: 0 }}
                                                        />
                                                    )}
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>
                    </>
                )}
            </div>

            {/* Fixed Pagination */}
            {filteredHistory.length > 0 && (
                <PaginationFooter
                    activePage={activePage}
                    totalPages={totalPages}
                    totalItems={allHistory.length}
                    filteredItems={filteredHistory}
                    hasActiveFilters={hasActiveFilters}
                    onPageChange={setActivePage}
                    pageStart={pageStart}
                    pageEnd={pageEnd}
                />
            )}

            <RequestDetailsModal
                open={showDetailsModal}
                onClose={() => {
                    setShowDetailsModal(false);
                    setSelectedRequest(null);
                }}
                request={selectedRequest}
                showApprovalActions={false}
            />

            <Confirm
                open={deleteConfirmOpen}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                content={
                    deleteTarget
                        ? `Are you sure you want to delete request ${deleteTarget}? This action cannot be undone.`
                        : `Are you sure you want to delete ${selectedIds.size} selected request(s)? This action cannot be undone.`
                }
                confirmButton={{ content: 'Delete', negative: true }}
                cancelButton="Cancel"
                size="small"
            />
        </div>
    );
};

export default SuperAdminHistory;